import { useState, useEffect } from "react";
import { format } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, MoreHorizontal, Check, Archive, RotateCcw, Mail, MessageCircle, Loader2, Trash2 } from "lucide-react";
import { FileGallery } from "./FileGallery";
import { ConciergeThread } from "@/components/concierge/ConciergeThread";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";

type Submission = Tables<"conversation_submissions"> & {
  reviewed_at?: string | null;
};

interface SubmissionCardProps {
  submission: Submission;
  onStatusChange: (id: string, status: string) => void;
  onDelete: (id: string) => void;
  defaultOpen?: boolean;
  forceOpen?: boolean;
}

const statusConfig = {
  new: { label: "New", variant: "default" as const, className: "bg-primary text-primary-foreground" },
  reviewed: { label: "Reviewed", variant: "secondary" as const, className: "bg-secondary text-secondary-foreground" },
  archived: { label: "Archived", variant: "outline" as const, className: "text-muted-foreground" },
};

export function SubmissionCard({ submission, onStatusChange, onDelete, defaultOpen = false, forceOpen }: SubmissionCardProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [showThread, setShowThread] = useState(false);
  const [conciergeId, setConciergeId] = useState<string | null>(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState(false);

  useEffect(() => {
    if (forceOpen !== undefined) setIsOpen(forceOpen);
  }, [forceOpen]);

  const config = statusConfig[submission.status as keyof typeof statusConfig] || statusConfig.new;
  const fileUrls = submission.file_urls || [];
  const messageSnippet = submission.message
    ? submission.message.length > 80
      ? submission.message.slice(0, 80) + "…"
      : submission.message
    : "No message";

  const handleToggleThread = async () => {
    if (showThread) {
      setShowThread(false);
      return;
    }

    if (conciergeId) {
      setShowThread(true);
      return;
    }

    setThreadLoading(true);
    setThreadError(false);

    const firstLine = submission.message?.split("\n")[0]?.trim() || "";
    
    const { data, error } = await supabase
      .from("concierge_requests")
      .select("id")
      .ilike("initial_description", firstLine)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error || !data) {
      setThreadError(true);
      setThreadLoading(false);
      return;
    }

    setConciergeId(data.id);
    setShowThread(true);
    setThreadLoading(false);
  };

  return (
    <Card className="transition-shadow hover:shadow-md">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger className="w-full text-left">
          <div className="flex items-center justify-between gap-3 px-4 py-3 group">
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-sm font-medium text-foreground whitespace-nowrap">
                {format(new Date(submission.submitted_at), "MMM d, yyyy")}
              </span>
              <Badge className={config.className}>{config.label}</Badge>
              {submission.email && (
                <span className="text-xs text-muted-foreground hidden sm:inline truncate">
                  {submission.email}
                </span>
              )}
              <span className="text-xs text-muted-foreground truncate hidden md:inline">
                {messageSnippet}
              </span>
            </div>
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="px-4 pb-4 pt-0 border-t border-border/50">
            <div className="flex flex-col gap-3 pt-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-medium text-foreground">
                    {format(new Date(submission.submitted_at), "MMM d, yyyy 'at' h:mm a")}
                  </span>
                  {submission.email && (
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Mail className="h-3 w-3" />
                      <span>{submission.email}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-1">
                  <Button
                    variant={showThread ? "secondary" : "ghost"}
                    size="sm"
                    className="gap-1.5 text-xs"
                    onClick={(e) => { e.stopPropagation(); handleToggleThread(); }}
                    disabled={threadLoading}
                  >
                    {threadLoading ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <MessageCircle className="h-3.5 w-3.5" />
                    )}
                    Reply
                  </Button>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {submission.status !== "reviewed" && (
                        <DropdownMenuItem onClick={() => onStatusChange(submission.id, "reviewed")}>
                          <Check className="mr-2 h-4 w-4" /> Mark as Reviewed
                        </DropdownMenuItem>
                      )}
                      {submission.status !== "archived" && (
                        <DropdownMenuItem onClick={() => onStatusChange(submission.id, "archived")}>
                          <Archive className="mr-2 h-4 w-4" /> Archive
                        </DropdownMenuItem>
                      )}
                      {submission.status !== "new" && (
                        <DropdownMenuItem onClick={() => onStatusChange(submission.id, "new")}>
                          <RotateCcw className="mr-2 h-4 w-4" /> Mark as New
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => onDelete(submission.id)}>
                        <Trash2 className="mr-2 h-4 w-4" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {submission.message && (
                <p className="text-sm text-foreground whitespace-pre-wrap">{submission.message}</p>
              )}

              {fileUrls.length > 0 && (
                <div className="border-t border-border pt-3">
                  <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Attachments ({fileUrls.length})
                  </div>
                  <FileGallery fileUrls={fileUrls} />
                </div>
              )}

              {threadError && (
                <div className="rounded-lg border border-border bg-muted/50 p-3 text-center text-sm text-muted-foreground">
                  No linked concierge request found for this submission.
                </div>
              )}

              {showThread && conciergeId && (
                <div className="border-t border-border pt-3">
                  <ConciergeThread conciergeRequestId={conciergeId} isAdmin />
                </div>
              )}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
